/**
 * Seed → Manual tab: placement tool (pattern / producer / injector) above a wells
 * table with the same columns as the layer wells table on the main page.
 */
import DeleteIcon from '@mui/icons-material/Delete';
import {
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef } from 'react';

import { CommitNumberField } from '@/components/Scenarios/CommitNumberField';
import {
  SEED_RATIOS,
  seedRatioKey,
  type SeedTool,
  type SeedWellKind,
  type SeedWellRow,
} from '@/utils/materialBalanceSeedManual';

const denseFieldSx = {
  width: '100%',
  '& .MuiInputBase-root': { fontSize: 12, height: 26 },
  '& .MuiInputBase-input': { py: '2px', px: '6px' },
  '& .MuiSelect-select': {
    py: '2px !important',
    px: '6px !important',
    display: 'flex',
    alignItems: 'center',
  },
} as const;

const denseCellSx = {
  py: '2px',
  px: '4px',
  fontSize: 12,
  lineHeight: 1.2,
  borderBottomColor: 'divider',
} as const;

const denseHeadCellSx = {
  ...denseCellSx,
  py: '4px',
  fontSize: 11,
  fontWeight: 600,
  bgcolor: 'background.paper',
} as const;

export interface SeedManualPanelProps {
  rows: SeedWellRow[];
  onRowsChange: (rows: SeedWellRow[]) => void;
  selectedId: string | null;
  onSelect: (id: string) => void;
  tool: SeedTool | '';
  onToolChange: (tool: SeedTool | '') => void;
  ratioKey: string;
  onRatioKeyChange: (key: string) => void;
  distanceM: number | null;
  onDistanceMChange: (m: number | null) => void;
  disabled?: boolean;
}

export const SeedManualPanel: React.FC<SeedManualPanelProps> = ({
  rows,
  onRowsChange,
  selectedId,
  onSelect,
  tool,
  onToolChange,
  ratioKey,
  onRatioKeyChange,
  distanceM,
  onDistanceMChange,
  disabled = false,
}) => {
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});

  useEffect(() => {
    if (!selectedId) return;
    rowRefs.current[selectedId]?.scrollIntoView({ block: 'nearest' });
  }, [selectedId]);

  const patch = <K extends keyof SeedWellRow>(id: string, key: K, value: SeedWellRow[K]) => {
    onRowsChange(
      rows.map((r) => {
        if (r.id !== id) return r;
        const next = { ...r, [key]: value };
        if (key === 'kind' && value === 'inj') next.q_start_td = null;
        return next;
      })
    );
  };

  const toolHint =
    tool === ''
      ? 'Choose a tool, then click inside the contour to place wells.'
      : tool === 'pattern'
        ? ratioKey && distanceM
          ? 'Click inside the contour to place the pattern. Parts overlapping existing wells are cut out.'
          : 'Set the ratio and the injector–producer distance.'
        : `Click inside the contour to place a ${tool === 'prod' ? 'producer' : 'injector'}.`;

  return (
    <Stack spacing={1.5} sx={{ minHeight: 0, height: '100%' }}>
      <Stack spacing={1} sx={{ flexShrink: 0 }}>
        <Stack direction='row' spacing={1}>
          <FormControl size='small' sx={{ flex: 1.3, minWidth: 0 }}>
            <InputLabel>Tool</InputLabel>
            <Select
              label='Tool'
              value={tool}
              disabled={disabled}
              onChange={(e) => onToolChange(e.target.value as SeedTool | '')}
            >
              <MenuItem value='pattern'>Multi-spot pattern</MenuItem>
              <MenuItem value='prod'>Producer</MenuItem>
              <MenuItem value='inj'>Injector</MenuItem>
            </Select>
          </FormControl>
          {tool === 'pattern' ? (
            <>
              <FormControl size='small' sx={{ flex: 1, minWidth: 0 }}>
                <InputLabel>Prod : Inj</InputLabel>
                <Select
                  label='Prod : Inj'
                  value={ratioKey}
                  disabled={disabled}
                  onChange={(e) => onRatioKeyChange(e.target.value)}
                >
                  {SEED_RATIOS.map((r) => (
                    <MenuItem key={seedRatioKey(r)} value={seedRatioKey(r)}>
                      {r.prod} : {r.inj}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <CommitNumberField
                size='small'
                label='Inj–prod (m)'
                value={distanceM}
                disabled={disabled}
                onCommit={(n) => onDistanceMChange(n != null && n > 0 ? n : null)}
                sx={{ flex: 1, minWidth: 0 }}
              />
            </>
          ) : null}
        </Stack>
        <Typography variant='caption' color='text.secondary'>
          {toolHint}
        </Typography>
      </Stack>

      <TableContainer
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Table
          size='small'
          stickyHeader
          sx={{
            width: '100%',
            tableLayout: 'fixed',
            '& .MuiTableCell-root': { overflow: 'hidden' },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...denseHeadCellSx, width: '16%' }}>id</TableCell>
              <TableCell sx={{ ...denseHeadCellSx, width: '15%' }}>type</TableCell>
              <TableCell sx={{ ...denseHeadCellSx, width: '12%' }}>start</TableCell>
              <TableCell sx={{ ...denseHeadCellSx, width: '12%' }}>end</TableCell>
              <TableCell sx={{ ...denseHeadCellSx, width: '13%' }}>x</TableCell>
              <TableCell sx={{ ...denseHeadCellSx, width: '13%' }}>y</TableCell>
              <TableCell sx={{ ...denseHeadCellSx, width: '11%' }}>q₀</TableCell>
              <TableCell sx={{ ...denseHeadCellSx, width: 32, px: 0 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={denseCellSx}>
                  <Typography variant='caption' color='text.secondary'>
                    No wells yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((w) => (
                <TableRow
                  key={w.id}
                  ref={(el) => {
                    rowRefs.current[w.id] = el;
                  }}
                  hover
                  selected={selectedId === w.id}
                  onClick={() => onSelect(w.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={denseCellSx}>
                    <TextField
                      size='small'
                      value={w.well_name}
                      disabled={disabled}
                      onChange={(e) => patch(w.id, 'well_name', e.target.value)}
                      sx={denseFieldSx}
                    />
                  </TableCell>
                  <TableCell sx={denseCellSx}>
                    <TextField
                      select
                      size='small'
                      value={w.kind}
                      disabled={disabled}
                      onChange={(e) => patch(w.id, 'kind', e.target.value as SeedWellKind)}
                      sx={denseFieldSx}
                    >
                      <MenuItem value='prod' sx={{ fontSize: 12 }}>
                        prod
                      </MenuItem>
                      <MenuItem value='inj' sx={{ fontSize: 12 }}>
                        inj
                      </MenuItem>
                    </TextField>
                  </TableCell>
                  <TableCell sx={denseCellSx}>
                    <CommitNumberField
                      size='small'
                      value={w.t0}
                      disabled={disabled}
                      formatCommitted={(n) => Math.round(n)}
                      onCommit={(n) => patch(w.id, 't0', n)}
                      sx={denseFieldSx}
                    />
                  </TableCell>
                  <TableCell sx={denseCellSx}>
                    <CommitNumberField
                      size='small'
                      value={w.t1}
                      disabled={disabled}
                      formatCommitted={(n) => Math.round(n)}
                      onCommit={(n) => patch(w.id, 't1', n)}
                      sx={denseFieldSx}
                    />
                  </TableCell>
                  <TableCell sx={denseCellSx}>
                    <CommitNumberField
                      size='small'
                      value={w.x}
                      disabled={disabled}
                      onCommit={(n) => patch(w.id, 'x', n)}
                      sx={denseFieldSx}
                    />
                  </TableCell>
                  <TableCell sx={denseCellSx}>
                    <CommitNumberField
                      size='small'
                      value={w.y}
                      disabled={disabled}
                      onCommit={(n) => patch(w.id, 'y', n)}
                      sx={denseFieldSx}
                    />
                  </TableCell>
                  <TableCell sx={denseCellSx}>
                    {w.kind === 'prod' ? (
                      <CommitNumberField
                        size='small'
                        placeholder='—'
                        value={w.q_start_td}
                        disabled={disabled}
                        onCommit={(n) => patch(w.id, 'q_start_td', n)}
                        sx={denseFieldSx}
                      />
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ ...denseCellSx, px: 0, width: 32 }}>
                    <IconButton
                      size='small'
                      disabled={disabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRowsChange(rows.filter((r) => r.id !== w.id));
                      }}
                      aria-label='Delete well'
                      sx={{ p: 0.25 }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};
